import { createApp, configureCompat } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import i18n from '@/libs/i18n'
import App from './App.vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import axiosIns from '@axios'
import setupCASL from '@/libs/acl'
import setupToast from '@/libs/toastification'
import vSelect from '@/libs/vue-select'
import { useAppConfigStore } from '@/stores/appConfigStore'
import { useAppStore } from '@/stores/appStore'

import VueMask from 'v-mask'
import PortalVue from 'portal-vue'

import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

import VueCookieAcceptDecline from 'vue-cookie-accept-decline'
import CountryFlag from 'vue-country-flag-next'

import '@core/assets/fonts/feather/iconfont.css'
import '@resources/scss/core.scss'
import '@resources/assets/scss/style.scss'
import 'vue-select/dist/vue-select.css'

configureCompat({
  MODE: 2,
  INSTANCE_LISTENERS: true,
})

const getStoredLocale = () => {
  try {
    const userData = localStorage.getItem('userData')
    if (userData) {
      const parsedUserData = JSON.parse(userData)
      if (parsedUserData?.locale) return parsedUserData.locale
    }
  } catch (error) {
    // Ignore malformed localStorage and keep fallback locale.
  }

  return localStorage.getItem('locale') || 'frch'
}

const setInitialLocale = locale => {
  if (i18n?.global?.locale?.value !== undefined) {
    i18n.global.locale.value = locale
    return
  }

  if (i18n?.global?.locale !== undefined) {
    i18n.global.locale = locale
    return
  }

  if (Object.prototype.hasOwnProperty.call(i18n, 'locale')) {
    i18n.locale = locale
  }
}

setInitialLocale(getStoredLocale())

window.Pusher = Pusher

if (process.env.MIX_APP_DEBUG === 'true') {
  Pusher.logToConsole = true
}

const echoOptions = {
  broadcaster: 'pusher',
  key: process.env.MIX_PUSHER_APP_KEY,

  host: process.env.MIX_PUSHER_HOST || window.location.hostname,
  wsHost: process.env.MIX_PUSHER_HOST || window.location.hostname,
  wsPort: 80,
  wssPort: 443,
  forceTLS: true,
  enabledTransports: ['ws', 'wss'],
  disableStats: true,
  cluster: 'mt1',
  authEndpoint: '/broadcasting/auth',
  authorizer: channel => ({
    authorize: (socketId, callback) => {
      axiosIns.post('/broadcasting/auth', {
        socket_id: socketId,
        channel_name: channel.name,
      })
        .then(res => callback(false, res.data))
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Broadcast auth failed:', err?.response?.status, err?.response?.data)
          callback(true, err)
        })
    },
  }),
}

const echo = new Echo(echoOptions)
window.Echo = echo

const pinia = createPinia()
const app = createApp(App)

const safeTranslate = (...args) => {
  if (args[0] === undefined || args[0] === null) {
    return ''
  }

  if (typeof i18n?.global?.t === 'function') {
    return i18n.global.t(...args)
  }

  if (typeof i18n?.t === 'function') {
    return i18n.t(...args)
  }

  return ''
}

app.use(pinia)
const appConfigStore = useAppConfigStore(pinia)
const appStore = useAppStore(pinia)
app.use(router)
app.use(VueMask)
app.use(PortalVue)

const hasResponsiveModalState = vm => {
  const componentData = vm.$data || {}

  return ['modalScrollable', 'modalCentered', 'modalClass']
    .every(key => Object.prototype.hasOwnProperty.call(componentData, key))
}

const syncResponsiveModalState = vm => {
  if (!hasResponsiveModalState(vm)) return

  const currentWidth = appStore.windowWidth || (typeof window !== 'undefined' ? window.innerWidth : 801)
  const isMobile = currentWidth <= 800

  vm.modalScrollable = isMobile
  vm.modalCentered = !isMobile
  vm.modalClass = isMobile ? 'modal-fullscreen' : ''
}

app.mixin({
  mounted() {
    if (!hasResponsiveModalState(this)) return

    syncResponsiveModalState(this)
    this.$watch(
      () => appStore.windowWidth,
      () => syncResponsiveModalState(this),
    )
  },
})

if (typeof i18n?.install === 'function') {
  app.use(i18n)
  app.config.globalProperties.$t = safeTranslate
} else {
  app.config.globalProperties.$i18n = i18n
  app.config.globalProperties.$t = safeTranslate
  app.config.globalProperties.$tc = (...args) => i18n.tc(...args)
  app.config.globalProperties.$te = (...args) => i18n.te(...args)
  app.config.globalProperties.$d = (...args) => i18n.d(...args)
  app.config.globalProperties.$n = (...args) => i18n.n(...args)
}

app.component('vue-cookie-accept-decline', VueCookieAcceptDecline)
app.component('country-flag', CountryFlag)
app.component('v-select', vSelect)
app.component('feather-icon', FeatherIcon)

setupCASL(app)
const appToast = setupToast(app)
window.$toast = appToast

app.config.globalProperties.$echo = echo
app.config.globalProperties.$http = axiosIns
app.config.globalProperties.$appConfigStore = appConfigStore
app.config.globalProperties.$appStore = appStore
app.provide('appConfigStore', appConfigStore)
app.provide('appStore', appStore)

app.mount('#app')
